<script setup>
import { ref, inject, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import TextNodes from "@Components/TextNodes/TextNodes.vue";
import Step from "@Components/Stepper/Step.vue";
import { TrashIcon, ArrowPathIcon } from "@heroicons/vue/20/solid";
import { useForm } from "@/form";

const props = defineProps(["modelValue", "step"]);
defineEmits(["update:modelValue", "next", "back", "skip"]);

const { t: __ } = useI18n();
const requestsSettings = inject("requestsSettings");

const nodes = ref();
const processing = ref(false);

const setProcessing = (value) => {
    if (value) {
        processing.value = true;
        nodes.value.setLoading(true);
    } else {
        processing.value = false;
        nodes.value.setLoading(false);
    }
};

const generate = async (keyword, length) => {
    if (length === undefined) {
        length = 1;
    }

    return await useForm({
        engine: "news",
        query: keyword,
        length: length,
        source_language: props.modelValue.modules.autopilot.source_language,
        language: props.modelValue.language,
    }).post("requests_generate");
};

const refresh = async () => {
    if (processing.value) return;

    if (requestsSettings.value.keyword === undefined || requestsSettings.value.keyword.length === 0) return;

    setProcessing(true);

    try {
        const response = await generate(requestsSettings.value.keyword, requestsSettings.value.now_length);
        nodes.value.replaceRows(response.requests);
    } catch (e) {
        console.log(e);
    }

    setProcessing(false);
};

const rewrite = async (title) => {
    return await useForm({
        title: title,
        engine: "news",
        language: props.modelValue.language,
    }).post("requests_rewrite");
};

const actions = {
    header: [],
    footer: [],
    row: [
        {
            icon: ArrowPathIcon,
            tooltip: __("Réécrire"),
            label: null,
            canRun: (action, { getRow }) => {
                // verify that the action is not already processing
                if (action.getProcessing()) return false;

                // verify that the row is not empty
                const row = getRow();
                if (row.title.length === 0) return false;

                return true;
            },
            onBegin: (action) => {
                action.setProcessing(true);
            },
            onHandle: async (action, { getRow, updateRow }) => {
                // call the rewrite
                try {
                    let row = getRow();
                    const response = await rewrite(row.title);
                    row.title = response.title;
                    updateRow(row);
                } catch (e) {}
            },
            onComplete: (action) => {
                action.setProcessing(false);
            },
        },
        {
            icon: TrashIcon,
            tooltip: __("Supprimer"),
            label: null,
            onHandle: (action, { deleteRow }) => {
                deleteRow();
            },
        },
    ],
};

onMounted(() => {
    if (props.modelValue.requests.length === 0) {
        refresh();
    }
});
</script>

<template>
    <Step class="px-4 lg:px-8 sm:px-6" :step="step" @next="$emit('next')" @back="$emit('back')" @skip="$emit('skip')">
        <template #header>
            <div class="mx-auto max-w-3xl">
                <h3 class="text-4xl font-semibold leading-tight text-center text-gray-900 dark:text-white">
                    {{ __("Vos titres") }}
                </h3>
            </div>
        </template>
        <div class="mx-auto flex flex-col">
            <TextNodes
                ref="nodes"
                v-model="modelValue.requests"
                :actions="actions"
                disable-new-rows
                :depth-enabled="modelValue.modules.wordpress.post_type === 'page'"
                sortable-enabled />
        </div>
    </Step>
</template>
